import React, { useState } from 'react';

import { Button, Input, Select, Space, Row, Col, Flex, Typography } from 'antd';
import { Formik } from 'formik';
import { Link } from 'react-router-dom';

import { DatetimePicker } from './DatetimePicker';
import { NumberInput } from './NumberInput';
import { BoxContractUpdateRequest } from '../hooks/useUpdateBoxContract';
import {
  BoxContract,
  BoxContractStates,
  PaymentMethods,
  PaymentProcessors,
  PaymentStatuses,
} from '../models/boxContract';
import { editBoxContractSchema } from '../schemas/boxContractSchema';
import FormRow from '@/components/forms/FormRow';
import ScopedCta from '@/components/ScopedCta';
import { scopes } from '@/config';
import {
  LpAccountAddressSelector,
  SearchAccount,
} from '@/features/lp-account-users';

const { Option } = Select;
const { Text } = Typography;

export interface EditBoxContractFormProps {
  contract: BoxContract;
  onSubmit: (contract: BoxContractUpdateRequest) => void | Promise<void>;
}

export function EditBoxContractForm({
  contract,
  onSubmit,
}: EditBoxContractFormProps) {
  const initialValues = getInitialValues(contract);
  const [showLinkAccount, setShowLinkAccount] = useState<boolean>(false);

  return (
    <Formik<EditBoxContractFormValues>
      initialValues={initialValues}
      validationSchema={editBoxContractSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        await onSubmit({
          ...values,
          numOfBox: parseInt(values.numOfBox),
          totalAmountInCents: parseFloat(values.totalAmount) * 100,
          email: values.accountId ? '' : values.email,
        });
        setSubmitting(false);
      }}
    >
      {({
        handleSubmit,
        handleReset,
        handleChange,
        handleBlur,
        values,
        isSubmitting,
        dirty,
        setFieldValue,
        setTouched,
        errors,
        touched,
        isValid,
      }) => (
        <form onSubmit={handleSubmit} onReset={handleReset} noValidate>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space>
              <Button onClick={() => setShowLinkAccount((prev) => !prev)}>
                {showLinkAccount ? 'Cancel' : 'Link Account'}
              </Button>
              {contract.accountId && (
                <Link to={`/lp-account/${contract.accountId}`}>
                  <Button>To LP-Account</Button>
                </Link>
              )}
            </Space>

            <FormRow>
              {showLinkAccount && (
                <Flex vertical style={{ width: '100%' }}>
                  <SearchAccount
                    accountId={touched.accountId ? values.accountId : ''}
                    setAccountId={(value) => {
                      setFieldValue('accountId', value);
                      setTouched({ accountId: true });
                    }}
                  />
                  {errors.accountId && touched.accountId && (
                    <Text type="danger" style={{ marginTop: '4px' }}>
                      {errors.accountId}
                    </Text>
                  )}
                </Flex>
              )}
              {values.accountId !== '' &&
                touched.accountId &&
                showLinkAccount && (
                  <Flex vertical style={{ width: '100%' }}>
                    <LpAccountAddressSelector
                      onChange={(value) => setFieldValue('addressId', value)}
                      accountId={values.accountId}
                      addressId={values.addressId}
                    />
                    {errors.addressId && touched.accountId && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {errors.addressId}
                      </Text>
                    )}
                  </Flex>
                )}
            </FormRow>

            {contract.accountId !== '' && (
              <FormRow>
                <Row gutter={16} style={{ width: '100%' }}>
                  <Col span={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text>First Name</Text>
                      <Input
                        name="firstName"
                        value={contract.firstName}
                        disabled
                      />
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text>Last Name</Text>
                      <Input
                        name="lastName"
                        value={contract.lastName}
                        disabled
                      />
                    </Space>
                  </Col>
                </Row>
              </FormRow>
            )}

            <FormRow>
              <Flex vertical style={{ width: '100%' }}>
                <Text>Email</Text>
                <Input
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={contract.accountId !== '' || showLinkAccount}
                  status={errors.email && touched.email ? 'error' : ''}
                />
                {errors.email && touched.email && (
                  <Text type="danger" style={{ marginTop: '4px' }}>
                    {errors.email}
                  </Text>
                )}
              </Flex>
            </FormRow>

            <FormRow>
              <Flex vertical style={{ width: '100%' }}>
                <Text>Accepted ToS version</Text>
                <Input
                  name="acceptedTosVersion"
                  value={values.acceptedTosVersion}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  status={
                    errors.acceptedTosVersion && touched.acceptedTosVersion
                      ? 'error'
                      : ''
                  }
                />
                {errors.acceptedTosVersion && touched.acceptedTosVersion && (
                  <Text type="danger" style={{ marginTop: '4px' }}>
                    {errors.acceptedTosVersion}
                  </Text>
                )}
              </Flex>
            </FormRow>

            <FormRow>
              <Row gutter={16} style={{ width: '100%' }}>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Activated At</Text>
                    <DatetimePicker
                      name="activatedAt"
                      id="activatedAt"
                      value={values.activatedAt}
                      dateFormat="dd/MM/yyyy HH:mm"
                      onBlur={handleBlur}
                      onChange={(date) => {
                        setFieldValue('activatedAt', date);
                      }}
                    />
                    {errors.activatedAt && touched.activatedAt && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {String(errors.activatedAt)}
                      </Text>
                    )}
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Contract State</Text>
                    <Select
                      value={values.state}
                      onChange={(value) => setFieldValue('state', value)}
                      onBlur={handleBlur}
                      style={{ width: '100%' }}
                      status={errors.state && touched.state ? 'error' : ''}
                    >
                      {BoxContractStates.map((value) => (
                        <Option key={value} value={value}>
                          {value}
                        </Option>
                      ))}
                    </Select>
                    {errors.state && touched.state && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {errors.state}
                      </Text>
                    )}
                  </Space>
                </Col>
              </Row>
            </FormRow>

            <FormRow>
              <Row gutter={16} style={{ width: '100%' }}>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Number of Boxes</Text>
                    <NumberInput
                      name="numOfBox"
                      precision={0}
                      value={values.numOfBox}
                      onChange={(value) => setFieldValue('numOfBox', value)}
                      onBlur={handleBlur}
                      status={
                        errors.numOfBox && touched.numOfBox ? 'error' : ''
                      }
                    />
                    {errors.numOfBox && touched.numOfBox && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {errors.numOfBox}
                      </Text>
                    )}
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Total Amount</Text>
                    <NumberInput
                      name="totalAmount"
                      precision={2}
                      value={values.totalAmount}
                      onChange={(value) =>
                        setFieldValue(
                          'totalAmount',
                          value !== null ? String(value) : ''
                        )
                      }
                      onBlur={handleBlur}
                      prefix="€"
                      status={
                        errors.totalAmount && touched.totalAmount ? 'error' : ''
                      }
                    />
                    {errors.totalAmount && touched.totalAmount && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {errors.totalAmount}
                      </Text>
                    )}
                  </Space>
                </Col>
              </Row>
            </FormRow>

            <FormRow>
              <Row gutter={16} style={{ width: '100%' }}>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                      Payment Status <Text type="danger">*</Text>
                    </Text>
                    <Select
                      value={values.paymentStatus}
                      onChange={(value) =>
                        setFieldValue('paymentStatus', value)
                      }
                      onBlur={handleBlur}
                      style={{ width: '100%' }}
                      status={
                        errors.paymentStatus && touched.paymentStatus
                          ? 'error'
                          : ''
                      }
                    >
                      {PaymentStatuses.map((value) => (
                        <Option key={value} value={value}>
                          {value}
                        </Option>
                      ))}
                    </Select>
                    {errors.paymentStatus && touched.paymentStatus && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {errors.paymentStatus}
                      </Text>
                    )}
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                      Payment Processor <Text type="danger">*</Text>
                    </Text>
                    <Select
                      value={values.paymentProcessor}
                      onChange={(value) =>
                        setFieldValue('paymentProcessor', value)
                      }
                      onBlur={handleBlur}
                      style={{ width: '100%' }}
                      status={
                        errors.paymentProcessor && touched.paymentProcessor
                          ? 'error'
                          : ''
                      }
                    >
                      {PaymentProcessors.map((value) => (
                        <Option key={value} value={value}>
                          {value}
                        </Option>
                      ))}
                    </Select>
                    {errors.paymentProcessor && touched.paymentProcessor && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {errors.paymentProcessor}
                      </Text>
                    )}
                  </Space>
                </Col>
              </Row>
            </FormRow>

            <FormRow>
              <Row gutter={16} style={{ width: '100%' }}>
                <Col span={8}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                      Payment Date <Text type="danger">*</Text>
                    </Text>
                    <DatetimePicker
                      name="paymentDate"
                      id="paymentDate"
                      value={values.paymentDate}
                      dateFormat="dd/MM/yyyy HH:mm"
                      onBlur={handleBlur}
                      onChange={(date) => {
                        setFieldValue('paymentDate', date);
                      }}
                    />
                    {errors.paymentDate && touched.paymentDate && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {String(errors.paymentDate)}
                      </Text>
                    )}
                  </Space>
                </Col>
                <Col span={8}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                      Payment Unique Id <Text type="danger">*</Text>
                    </Text>
                    <Input
                      name="paymentUniqueId"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.paymentUniqueId}
                      status={
                        errors.paymentUniqueId && touched.paymentUniqueId
                          ? 'error'
                          : ''
                      }
                    />
                    {errors.paymentUniqueId && touched.paymentUniqueId && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {errors.paymentUniqueId}
                      </Text>
                    )}
                  </Space>
                </Col>
                <Col span={8}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Payment Received At</Text>
                    <DatetimePicker
                      name="paymentReceivedAt"
                      id="paymentReceivedAt"
                      value={values.paymentReceivedAt}
                      dateFormat="dd/MM/yyyy HH:mm"
                      onBlur={handleBlur}
                      onChange={(date) => {
                        setFieldValue('paymentReceivedAt', date);
                      }}
                    />
                    {errors.paymentReceivedAt && touched.paymentReceivedAt && (
                      <Text type="danger" style={{ marginTop: '4px' }}>
                        {String(errors.paymentReceivedAt)}
                      </Text>
                    )}
                  </Space>
                </Col>
              </Row>
            </FormRow>

            <FormRow>
              <Flex vertical style={{ width: '100%' }}>
                <Text>
                  Payment Method <Text type="danger">*</Text>
                </Text>
                <Select
                  value={values.paymentMethod}
                  onChange={(value) => setFieldValue('paymentMethod', value)}
                  onBlur={handleBlur}
                  style={{ width: '100%' }}
                  status={
                    errors.paymentMethod && touched.paymentMethod ? 'error' : ''
                  }
                >
                  {PaymentMethods.map((value) => (
                    <Option key={value} value={value}>
                      {value}
                    </Option>
                  ))}
                </Select>
                {errors.paymentMethod && touched.paymentMethod && (
                  <Text type="danger" style={{ marginTop: '4px' }}>
                    {errors.paymentMethod}
                  </Text>
                )}
              </Flex>
            </FormRow>

            <Space style={{ marginTop: '16px' }}>
              <ScopedCta
                component={Button}
                size="small"
                type="primary"
                htmlType="submit"
                disabled={isSubmitting || !dirty || !isValid}
                requiredScopes={[scopes.MANAGE_BUY_N_RENT_CONTRACT]}
              >
                Save
              </ScopedCta>
              <Button
                size="small"
                htmlType="reset"
                disabled={isSubmitting || !dirty}
              >
                Reset
              </Button>
            </Space>
          </Space>
        </form>
      )}
    </Formik>
  );
}

interface EditBoxContractFormValues {
  paymentStatus: string;
  paymentProcessor: string;
  paymentDate: Date;
  paymentUniqueId: string;
  paymentMethod: string;
  paymentReceivedAt: Date;
  state: string;
  acceptedTosVersion: string;
  numOfBox: string;
  totalAmount: string;
  email: string;
  accountId: string;
  addressId: string;
  activatedAt: Date;
}

function getInitialValues(contract: BoxContract): EditBoxContractFormValues {
  return {
    paymentStatus: contract.paymentStatus,
    paymentProcessor: contract.paymentProcessor,
    paymentDate: contract.paymentDate,
    paymentUniqueId: contract.paymentUniqueId,
    paymentMethod: contract.paymentMethod,
    paymentReceivedAt: contract.paymentReceivedAt,
    state: contract.state,
    acceptedTosVersion: contract.acceptedTosVersion,
    numOfBox: contract.numOfBox.toString(),
    totalAmount: (contract.totalAmountInCents / 100).toFixed(2),
    email: contract.email,
    accountId: contract.accountId,
    addressId: contract.address.id,
    activatedAt: contract.activatedAt,
  };
}
